import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TablePagination,
  TextField,
} from '@mui/material';
import type { RegistryPage, RegistryResource } from '../../../api/src/registry/registry.types';
import { api } from '../api/client';
import { EditorOnly } from '../components/EditorOnly';
import { Stack, Typography } from '../components/mui-system';
import { fakeError } from '../components/fakes-presentation';
import { RegistryResourceDialog } from './RegistryResourceDialog';

export function SerpCompetitorsDialog({
  workspaceId,
  onClose,
  onChanged,
}: {
  workspaceId: string;
  onClose(): void;
  onChanged(): Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [version, setVersion] = useState(0);
  const [data, setData] = useState<RegistryPage | null>(null);
  const [error, setError] = useState('');
  const [edit, setEdit] = useState<RegistryResource | 'new' | null>(null);
  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    void api
      .registry(workspaceId, { role: 'competitor', search, page: page + 1 })
      .then((result) => {
        if (live) {
          setData(result);
          setPage(result.page - 1);
        }
      })
      .catch((e) => {
        if (live) setError(fakeError(e));
      });
    return () => {
      live = false;
    };
  }, [workspaceId, search, page, version]);
  return (
    <>
      <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
        <DialogTitle>Конкуренты</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Typography fontSize={13} color="text.secondary">
              Ресурсы с ролью «Конкурент» из общего Реестра. Название — компания или бренд. Домен
              охватывает его страницы; для общей платформы добавьте конкретный канал или ссылку.
            </Typography>
            <TextField
              label="Найти конкурента"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            {error && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" onClick={() => setVersion((v) => v + 1)}>
                    Повторить
                  </Button>
                }
              >
                {error}
              </Alert>
            )}
            {!data && !error && <LinearProgress />}
            {data?.items.map((row) => (
              <Stack
                key={row.key}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
              >
                <Stack sx={{ minWidth: 0 }}>
                  <Typography fontWeight={650}>{row.title}</Typography>
                  <Typography
                    fontSize={12}
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {row.address}
                  </Typography>
                </Stack>
                <Button onClick={() => setEdit(row)}>Карточка</Button>
              </Stack>
            ))}
            {data?.total === 0 && (
              <Typography color="text.secondary">
                {search
                  ? 'Конкуренты не найдены.'
                  : 'Добавьте первого конкурента — его ресурсы получат отметку в матрице.'}
              </Typography>
            )}
            {!!data && data.total > data.pageSize && (
              <TablePagination
                component="div"
                count={data.total}
                page={data.page - 1}
                rowsPerPage={data.pageSize}
                rowsPerPageOptions={[]}
                onPageChange={(_, value) => setPage(value)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Закрыть</Button>
          <EditorOnly permissions="registry.create">
            <Button variant="contained" onClick={() => setEdit('new')}>
              Добавить конкурента
            </Button>
          </EditorOnly>
        </DialogActions>
      </Dialog>
      {edit && (
        <RegistryResourceDialog
          workspaceId={workspaceId}
          resource={edit === 'new' ? undefined : edit}
          initialKind="domain"
          initialValues={{ ownership: 'third_party', roles: ['competitor'] }}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            setVersion((v) => v + 1);
            void onChanged().catch((e) => setError(fakeError(e)));
          }}
        />
      )}
    </>
  );
}
